import React, { useEffect, useState, useCallback } from "react";
import { MapPin, Navigation } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ListingFormValues } from "@/components/listings/filters/FilterSchema";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useProvinces,
  useDistrictsByProvinceId,
  useCitiesByDistrictId,
  useCityById, // Import useCityById
  useDistrictById, // Import useDistrictById
} from "@/hooks/useLocations";
import { Province, District, City } from "@/lib/types";

// Removed local interfaces

interface LocationStepProps {
  form: UseFormReturn<ListingFormValues>;
}

const LocationStep = ({ form }: LocationStepProps) => {
  const formLocation = form.watch("location");
  const [isInitialLoad, setIsInitialLoad] = useState(true); // State to track initial load

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: districts, isLoading: isLoadingDistricts } =
    useDistrictsByProvinceId(formLocation.provinceId);
  const { data: cities, isLoading: isLoadingCities } = useCitiesByDistrictId(
    formLocation.districtId
  );

  // Fetch city details if city_id is present and it's an initial load
  const { data: cityData, isLoading: isLoadingCityData } = useCityById(
    formLocation.city_id
  );

  // Fetch district details if districtId is present and it's an initial load
  const { data: districtData, isLoading: isLoadingDistrictData } =
    useDistrictById(formLocation.districtId);

  useEffect(() => {
    if (
      isInitialLoad &&
      formLocation.city_id &&
      cityData &&
      !isLoadingCityData &&
      districtData && // Ensure districtData is also available
      !isLoadingDistrictData &&
      provinces && // Ensure provinces are loaded
      districts && // Ensure districts are loaded
      cities // Ensure cities are loaded
    ) {
      // Set district and province IDs based on fetched city data
      if (cityData.district_id && !formLocation.districtId) {
        form.setValue("location.districtId", cityData.district_id, {
          shouldValidate: false,
        });
      }
      if (districtData.province_id && !formLocation.provinceId) {
        form.setValue("location.provinceId", districtData.province_id, {
          shouldValidate: false,
        });
      }

      // Now set the names based on the fetched data
      const selectedProvince = provinces.find(
        (p) => p.id === districtData.province_id
      );
      const selectedDistrict = districts.find(
        (d) => d.id === cityData.district_id
      );
      const selectedCity = cities.find((c) => c.id === cityData.id);

      form.setValue("location.province", selectedProvince?.name || "", {
        shouldValidate: false,
      });
      form.setValue("location.district", selectedDistrict?.name || "", {
        shouldValidate: false,
      });
      form.setValue("location.city", selectedCity?.name || "", {
        shouldValidate: false,
      });

      setIsInitialLoad(false); // Mark initial load as complete
    }
  }, [
    isInitialLoad,
    formLocation.city_id,
    cityData,
    isLoadingCityData,
    districtData,
    isLoadingDistrictData,
    provinces,
    districts,
    cities,
    form,
    formLocation.provinceId,
    formLocation.districtId,
  ]);

  const handleProvinceChange = useCallback(
    (value: string) => {
      const id = Number(value);
      const newProvinceId = isNaN(id) ? undefined : id;
      form.setValue("location.provinceId", newProvinceId, {
        shouldValidate: true,
      });
      // Find the province name from the 'provinces' array
      const selectedProvince = provinces?.find((p) => p.id === newProvinceId);
      form.setValue("location.province", selectedProvince?.name || "", {
        shouldValidate: false,
      });

      form.setValue("location.districtId", undefined, {
        shouldValidate: false,
      });
      form.setValue("location.city_id", undefined, { shouldValidate: false });
      form.setValue("location.district", "", { shouldValidate: false });
      form.setValue("location.city", "", { shouldValidate: false });
    },
    [form, provinces]
  );

  const handleDistrictChange = useCallback(
    (value: string) => {
      const id = Number(value);
      const newDistrictId = isNaN(id) ? undefined : id;
      form.setValue("location.districtId", newDistrictId, {
        shouldValidate: true,
      });
      // Find the district name from the 'districts' array
      const selectedDistrict = districts?.find((d) => d.id === newDistrictId);
      form.setValue("location.district", selectedDistrict?.name || "", {
        shouldValidate: false,
      });

      form.setValue("location.city_id", undefined, { shouldValidate: false });
      form.setValue("location.city", "", { shouldValidate: false });
    },
    [form, districts]
  );

  const handleCityChange = useCallback(
    (value: string) => {
      const id = Number(value);
      form.setValue("location.city_id", isNaN(id) ? undefined : id, {
        shouldValidate: true,
      });
      // Find the city name from the 'cities' array
      const selectedCity = cities?.find((c) => c.id === id);
      form.setValue("location.city", selectedCity?.name || "", {
        shouldValidate: false,
      });
    },
    [form, cities]
  );

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          form.setValue("location.latitude", latitude, {
            shouldValidate: true,
          });
          form.setValue("location.longitude", longitude, {
            shouldValidate: true,
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
          // Optionally, show a toast message to the user
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
      // Optionally, show a toast message to the user
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-propertylk-blue" />
            Location Information
          </CardTitle>
          <CardDescription>
            Enter the location details of your property. Select Province,
            District, and City.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Province Select */}
            <FormItem>
              <FormLabel>
                Province <span className="text-red-500">*</span>
              </FormLabel>
              {isLoadingProvinces ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  onValueChange={handleProvinceChange}
                  value={formLocation.provinceId?.toString() || ""}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {provinces?.map((province) => (
                      <SelectItem key={province.id} value={String(province.id)}>
                        {province.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FormMessage />{" "}
              {/* This message won't be tied to form.control directly */}
            </FormItem>

            {/* District Select */}
            <FormItem>
              <FormLabel>
                District <span className="text-red-500">*</span>
              </FormLabel>
              {isLoadingDistricts ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  onValueChange={handleDistrictChange}
                  value={formLocation.districtId?.toString() || ""}
                  disabled={!formLocation.provinceId || districts?.length === 0}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select district" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {districts?.map((district) => (
                      <SelectItem key={district.id} value={String(district.id)}>
                        {district.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FormMessage />{" "}
              {/* This message won't be tied to form.control directly */}
            </FormItem>

            {/* City Select */}
            <FormField
              control={form.control}
              name="location.city_id" // Changed to city_id
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    City / Town <span className="text-red-500">*</span>
                  </FormLabel>
                  {isLoadingCities ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      onValueChange={handleCityChange}
                      value={field.value?.toString() || ""} // Ensure value is string for Select
                      disabled={
                        !formLocation.districtId || cities?.length === 0
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select city/town" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {cities?.map((city) => (
                          <SelectItem key={city.id} value={String(city.id)}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="location.address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street Address</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 123 Galle Road" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="location.latitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Latitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any" // Allow more precision
                      placeholder="e.g., 6.9271"
                      {...field}
                      value={
                        field.value === null || field.value === undefined
                          ? ""
                          : field.value
                      } // Handle null/undefined for controlled input
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ""
                            ? null
                            : parseFloat(e.target.value)
                        )
                      } // Allow clearing
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location.longitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any" // Allow more precision
                      placeholder="e.g., 79.8612"
                      {...field}
                      value={
                        field.value === null || field.value === undefined
                          ? ""
                          : field.value
                      } // Handle null/undefined
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ""
                            ? null
                            : parseFloat(e.target.value)
                        )
                      } // Allow clearing
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full md:w-auto flex items-center gap-2"
            onClick={getCurrentLocation}
          >
            <Navigation className="h-4 w-4" />
            Get Current Location
          </Button>

          <div className="h-40 bg-gray-100 rounded-md flex items-center justify-center">
            <p className="text-gray-500 text-sm">
              Map component will be implemented here
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LocationStep;
